// src/pages/StudentDashboard.jsx
// 学生ダッシュボード
//
// 並べ方は2種類ある（「表示の設定」で人ごとに切り替え、既定は標準）【2026-09-18】
//   標準 (priority) … 学生がすぐ知りたい順に置く
//                     1層 今週の変更（休講・代講）／出席率が心配な科目
//                     2層 全体の出席状況
//                     3層 科目別・今週の授業・履歴（折りたたみ）
//   元の表示 (classic) … 2026-09-17 までの並び。統計4枚 → 授業別の表 → 履歴 → 今後の授業。
//                        こちらは中身を変えていない。
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useDashboardSettings from '../hooks/useDashboardSettings';
import { rateColor } from '../components/StudentCustomList';
import {
  QuickLinks, StatTiles, StatTile, Donut, Fold,
  StatusBadge, EmptyState, TableWrap,
} from '../components/DashboardParts';

// よく使う画面。学生はスマホで開くことが多く、サイドバーが☰の中に隠れるので、
// 行き先をダッシュボードに出しておく。
const STUDENT_LINKS = [
  { to: '/attendance',    icon: 'check-square', label: '出席確認' },
  { to: '/notifications', icon: 'bell',         label: 'お知らせ' },
  { to: '/app-download',  icon: 'smartphone',   label: 'アプリDL' },
  { to: '/profile',       icon: 'settings',     label: 'アカウント設定' },
];

export default function StudentDashboard({ user }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [dash] = useDashboardSettings();

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/dashboard/student', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => {
        if (d.success) setData(d.dashboard);
        else setErr(d.message || 'ダッシュボード取得失敗');
      })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-container"><div className="card"><p>読み込み中...</p></div></div>;
  if (err)     return <div className="page-container"><div className="card"><p style={{color:'#c62828'}}>{err}</p></div></div>;

  const { summary = {}, classes = [], recent_attendance = [], upcoming_classes = [] } = data || {};
  const rate = summary.overall_attendance_rate || 0;

  const classic = dash.layout === 'classic';

  // 並べ方の切り替えはアカウント設定の「画面の表示」に置いてある【2026-09-18】
  const header = (
    <div className="page-header">
      <h1>{classic ? `ようこそ、${user.name || user.student_id} さん` : `${user.name || user.student_id} さん`}</h1>
      <p>{classic ? 'あなたの出席状況サマリー' : `履修 ${summary.enrolled_classes || 0}科目`}</p>
    </div>
  );

  // ── 元の表示 ───────────────────────────────────────────────
  if (classic) {
    const rc = rate >= 80 ? '#22c55e' : rate >= 60 ? '#f59e0b' : '#ef4444';
    return (
      <div className="page-container">
        {header}

        <div className="stats-grid">
          <StatCard label="登録授業数" value={summary.enrolled_classes || 0} unit="科目" color="#3b82f6"/>
          <StatCard label="出席日数"   value={summary.present_days || 0}     unit="日"   color="#22c55e"/>
          <StatCard label="遅刻日数"   value={summary.late_days || 0}        unit="日"   color="#f59e0b"/>
          <StatCard label="総合出席率" value={rate + '%'}                    unit="全授業" color={rc}/>
        </div>

        <div className="card">
          <div className="card-header"><h2>授業別出席率</h2></div>
          {classes.length === 0
            ? <EmptyState text="まだ授業が登録されていません"/>
            : <ClassesTable rows={classes}/>}
        </div>

        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>最近の出席履歴</h2>
            <Link to="/attendance" className="btn-link">すべて見る →</Link>
          </div>
          {recent_attendance.length === 0
            ? <EmptyState text="出席履歴がありません"/>
            : <HistoryTable rows={recent_attendance}/>}
        </div>

        {upcoming_classes.length > 0 && (
          <div className="card">
            <div className="card-header"><h2>今後の授業（今週）</h2></div>
            <UpcomingList rows={upcoming_classes}/>
          </div>
        )}
      </div>
    );
  }

  // ── 標準（重要度順） ───────────────────────────────────────
  const changes = upcoming_classes.filter(c => c.change_type === 'cancelled' || c.change_type === 'substitute');
  const risks   = buildRisks(classes);
  // 欠席回数は API に無いので、科目ごとの「実施回数 − 出席回数」を足して出す
  const held    = classes.reduce((n, c) => n + (Number(c.total_sessions) || 0), 0);
  const absent  = classes.reduce(
    (n, c) => n + Math.max(0, (Number(c.total_sessions) || 0) - (Number(c.attended_sessions) || 0)), 0);

  return (
    <div className="page-container dash-priority">
      {header}

      {/* 1層: 今週の変更 */}
      {changes.length > 0 && (
        <div className="card dash-alert-card warn">
          <div className="card-header">
            <h2>今週の変更 <span className="dash-count warn">{changes.length}</span></h2>
          </div>
          {changes.map((c, i) => (
            <div key={i} className={'dash-change' + (c.change_type === 'substitute' ? ' sub' : '')}>
              <span className="dash-change-when">
                {c.day_name}
                {c.session_date && ' ' + String(c.session_date).slice(5).replace('-', '/')}
              </span>
              <span className="dash-change-what">
                <b>{c.name}</b>
                {c.change_type === 'cancelled'
                  ? ' ─ 休講'
                  : ' ─ 代講' + (c.substitute_teacher_name ? '（' + c.substitute_teacher_name + '）' : '')}
                {c.location ? ' ・ ' + c.location : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* 1層: 心配な科目 */}
      {risks.length > 0 && (
        <div className="card dash-alert-card crit">
          <div className="card-header">
            <h2>出席率が心配な科目 <span className="dash-count crit">{risks.length}</span></h2>
          </div>
          {risks.map(r => (
            <div key={r.id} className={'dash-risk' + (r.level === 'warn' ? ' w' : '')}>
              <div className="dash-risk-top">
                <span className="dash-risk-name">{r.name}</span>
                <span className="dash-risk-rate">{Math.round(r.rate)}%</span>
              </div>
              <div className="dash-risk-msg">
                {r.held}回中 <b>{r.attended}回</b> 出席（欠席 {r.absent}回）。
                次を欠席すると <b>{r.ifAbsent}%</b>、出席すれば <b>{r.ifPresent}%</b> になります。
              </div>
              <div className="dash-bar">
                <span style={{ width: Math.min(100, r.rate) + '%', background: rateColor(r.rate) }}/>
              </div>
            </div>
          ))}
        </div>
      )}

      <QuickLinks items={STUDENT_LINKS}/>

      {/* 2層 */}
      <div className="card">
        <div className="card-header"><h2>全体の出席状況</h2></div>
        <div className="dash-ring-row">
          <Donut rate={rate} color={rateColor(rate)}/>
          <div className="dash-ring-legend">
            <div><b>{summary.present_days || 0}</b> 回 出席</div>
            <div><b>{summary.late_days || 0}</b> 回 遅刻</div>
            <div><b>{absent}</b> 回 欠席</div>
            <div className="dash-ring-note">
              全{held}回中 ・ {summary.enrolled_classes || 0}科目合計
            </div>
          </div>
        </div>
      </div>

      <StatTiles>
        <StatTile label="履修科目"  value={summary.enrolled_classes || 0} unit="科目"/>
        <StatTile label="出席"      value={summary.present_days || 0}     unit="回"/>
        <StatTile label="遅刻"      value={summary.late_days || 0}        unit="回"/>
        <StatTile label="欠席"      value={absent}                        unit="回"
                  color={absent > 0 ? '#b45309' : undefined}/>
      </StatTiles>

      {/* 3層 */}
      <Fold title="科目別の出席率" count={classes.length + '科目'}>
        {classes.length === 0
          ? <EmptyState text="まだ授業が登録されていません"/>
          : <ClassesTable rows={classes}/>}
      </Fold>

      {upcoming_classes.length > 0 && (
        <Fold title="今週の授業" count={upcoming_classes.length + '件'}>
          <UpcomingList rows={upcoming_classes}/>
        </Fold>
      )}

      <Fold title="最近の出席履歴" count={'直近' + recent_attendance.length + '件'}>
        {recent_attendance.length === 0
          ? <EmptyState text="出席履歴がありません"/>
          : <>
              <HistoryTable rows={recent_attendance}/>
              <div style={{ marginTop: '0.6rem' }}>
                <Link to="/attendance" className="btn-link">すべて見る →</Link>
              </div>
            </>}
      </Fold>
    </div>
  );
}

// =========================================================================
//  心配な科目
//    80%未満を拾い、60%未満を赤にする（学生一覧の rateColor と同じ区切り）。
//    「次を休むとどうなるか」は attended / (held + 1) で出せる。
//    学期末までの残り回数が要る「あと何回休めるか」は、予定セッション数が
//    APIに無いので出していない（要API追加）。
// =========================================================================
function buildRisks(classes) {
  return classes
    .map(c => {
      const held     = Number(c.total_sessions) || 0;
      const attended = Number(c.attended_sessions) || 0;
      const rate     = Number(c.attendance_rate) || 0;
      return {
        id: c.id, name: c.name, held, attended, rate,
        absent: Math.max(0, held - attended),
        level: rate < 60 ? 'crit' : 'warn',
        ifAbsent:  held > 0 ? Math.round((attended / (held + 1)) * 100) : 0,
        ifPresent: held > 0 ? Math.round(((attended + 1) / (held + 1)) * 100) : 100,
      };
    })
    .filter(r => r.held > 0 && r.rate < 80)
    .sort((a, b) => a.rate - b.rate);
}

// =========================================================================
//  表（2つの並べ方で使い回す）
// =========================================================================
function ClassesTable({ rows }) {
  return (
    <TableWrap>
      <table className="data-table">
        <thead><tr><th>授業名</th><th>学期</th><th>出席率</th><th>出席/総数</th><th>進捗</th></tr></thead>
        <tbody>
          {rows.map(c => {
            const r = parseFloat(c.attendance_rate) || 0;
            const color = rateColor(r);
            return (
              <tr key={c.id}>
                <td><strong>{c.name}</strong></td>
                <td>{c.semester || '-'}</td>
                <td><strong style={{ color }}>{c.attendance_rate}%</strong></td>
                <td>{c.attended_sessions} / {c.total_sessions}</td>
                <td>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: r + '%', background: color }}/>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableWrap>
  );
}

function HistoryTable({ rows }) {
  return (
    <TableWrap>
      <table className="data-table">
        <thead><tr><th>日付</th><th>授業</th><th>教室</th><th>状態</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.attendance_date}</td>
              <td>{r.class_name}</td>
              <td>{r.location || '-'}</td>
              <td><StatusBadge status={r.status}/></td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function UpcomingList({ rows }) {
  return (
    <div className="upcoming-list">
      {rows.map((c, i) => {
        const cancelled  = c.change_type === 'cancelled';
        const substitute = c.change_type === 'substitute';
        const borderColor = cancelled ? '#ef4444' : substitute ? '#f59e0b' : '#3b82f6';
        return (
          <div key={i} className="upcoming-item" style={{
            borderLeftColor: borderColor,
            opacity: cancelled ? 0.65 : 1
          }}>
            <div className="upcoming-day">
              {c.day_name}
              {c.session_date && (
                <div style={{ fontSize: '0.72rem', color: '#888', fontWeight: 'normal' }}>
                  {String(c.session_date).slice(5).replace('-', '/')}
                </div>
              )}
            </div>
            <div className="upcoming-info">
              <div className="upcoming-name" style={{ textDecoration: cancelled ? 'line-through' : 'none' }}>
                {c.name}
                {cancelled && (
                  <span style={{
                    marginLeft: '0.5rem', padding: '0.1rem 0.5rem',
                    background: '#fee2e2', color: '#991b1b',
                    borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700
                  }}>休講</span>
                )}
                {substitute && (
                  <span style={{
                    marginLeft: '0.5rem', padding: '0.1rem 0.5rem',
                    background: '#fef3c7', color: '#92400e',
                    borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700
                  }}>代講{c.substitute_teacher_name ? '・' + c.substitute_teacher_name : ''}</span>
                )}
              </div>
              <div className="upcoming-meta">
                {c.start_time} 〜 {c.end_time}
                {c.location ? ' ・ ' + c.location : ''}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── 元の表示でしか使わない部品 ─────────────────────────────────
function StatCard({ label, value, unit, color }) {
  return (
    <div className="stat-card" style={{ borderTop: '4px solid ' + color }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-unit">{unit}</div>
    </div>
  );
}
